import { useEffect } from "react";
import { atom, useRecoilState, useRecoilValue } from "recoil";

import { Agent, InvestmentStyleDiagnosis, ProposalMatching, User } from "@/api/generated";
import { LimitedPropertyWithAgent } from "@/components/property/LimitedPropertiesSection";
import { UserState } from "@/view_models/atoms";

import { HomeInvestmentStyleDiagnosisStatus } from "./useHomeInvestmentStyleDiagnosis";

interface HomeDisplayData {
  investmentStyle: { status: HomeInvestmentStyleDiagnosisStatus; diagnosis: InvestmentStyleDiagnosis | null };
  proposalMatchings: ProposalMatching[];
  compatibilityAgents: Agent[];
  limitedProperties: LimitedPropertyWithAgent[];
}

interface HomeDisplayInput extends HomeDisplayData {
  proposalMatchingsLoading: boolean;
  compatibilityAgentsLoading: boolean;
  limitedLoading: boolean;
}

// 永続化せず、同じ UserState の間だけ直前の表示を保持する。ユーザー切替・再ログイン・
// プロフィール更新で UserState が変わった場合は、以前の診断や相性を引き継がない。
const homeDisplayState = atom<{ user: User; data: Partial<HomeDisplayData> } | null>({
  key: "mypage/homeDisplayData",
  default: null,
});
const NO_AGENTS: Agent[] = [];

// GET は各取得フックが毎回行う。表示だけを保持し、応答後（404・取得失敗を含む）は
// 新しい結果に置き換えることで、再訪時の「一度消えてから差し込まれる」動きを防ぐ。
export const useHomeDisplayData = (input: HomeDisplayInput): HomeDisplayInput => {
  const user = useRecoilValue(UserState);
  const [cached, setCached] = useRecoilState(homeDisplayState);
  const previous = cached && cached.user === user ? cached.data : {};
  const { status, diagnosis } = input.investmentStyle;
  const {
    proposalMatchings, proposalMatchingsLoading, compatibilityAgents, compatibilityAgentsLoading,
    limitedProperties, limitedLoading,
  } = input;
  // 相性プランナーは投資スタイル診断の状態に依存しない（登録時必須の投資力診断で採点される）。
  // 取得中は前回の表示を保ち、応答後に新しい結果へ置き換える
  const agentsPending = compatibilityAgentsLoading;

  useEffect(() => {
    setCached(current => {
      if (!user) return null;
      const before = current && current.user === user ? current.data : {};
      const data: Partial<HomeDisplayData> = {
        investmentStyle: status === "loading" ? before.investmentStyle : { status, diagnosis },
        proposalMatchings: proposalMatchingsLoading ? before.proposalMatchings : proposalMatchings,
        compatibilityAgents: agentsPending ? before.compatibilityAgents : compatibilityAgents,
        limitedProperties: limitedLoading ? before.limitedProperties : limitedProperties,
      };
      if (
        current?.user === user && before.investmentStyle?.status === data.investmentStyle?.status &&
        before.investmentStyle?.diagnosis === data.investmentStyle?.diagnosis &&
        before.proposalMatchings === data.proposalMatchings && before.compatibilityAgents === data.compatibilityAgents &&
        before.limitedProperties === data.limitedProperties
      ) return current;
      return { user, data };
    });
  }, [
    user, status, diagnosis, proposalMatchings, proposalMatchingsLoading, agentsPending, compatibilityAgents,
    limitedProperties, limitedLoading, setCached,
  ]);

  return {
    investmentStyle: status === "loading" ? previous.investmentStyle ?? input.investmentStyle : input.investmentStyle,
    proposalMatchings: proposalMatchingsLoading ? previous.proposalMatchings ?? proposalMatchings : proposalMatchings,
    proposalMatchingsLoading: proposalMatchingsLoading && previous.proposalMatchings === undefined,
    compatibilityAgents: agentsPending ? previous.compatibilityAgents ?? NO_AGENTS : compatibilityAgents,
    compatibilityAgentsLoading: agentsPending && previous.compatibilityAgents === undefined,
    limitedProperties: limitedLoading ? previous.limitedProperties ?? limitedProperties : limitedProperties,
    limitedLoading: limitedLoading && previous.limitedProperties === undefined,
  };
};
